import React, { useEffect, useMemo, useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, Filter, Plus, Search } from 'lucide-react';
import { api, extractError } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { MONTHS_DE, WEEKDAYS_SHORT, TYPE_BAR, monthRangeIso } from './constants';

const MIN_CELL_W = 40;
const NAME_W = 200;
const ROW_H = 40;

export default function AbsenceTimeline({ employees, costCenters, canCreate, onOpenCreate, onOpenEdit, reloadKey }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [ranges, setRanges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [kstFilter, setKstFilter] = useState('all');
  const scrollRef = useRef(null);
  const [cellW, setCellW] = useState(MIN_CELL_W);

  const { from, to, days } = useMemo(() => monthRangeIso(year, month), [year, month]);

  // Tagesspalten dynamisch so breit machen, dass sie die volle Breite ausnutzen
  useEffect(() => {
    const measure = () => {
      const el = scrollRef.current;
      if (!el) return;
      const avail = el.clientWidth - NAME_W;
      if (avail <= 0) return;
      setCellW(Math.max(MIN_CELL_W, Math.floor(avail / days)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (scrollRef.current) ro.observe(scrollRef.current);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [days]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/absences/grouped', { params: { from_date: from, to_date: to } });
      setRanges(r.data);
    } catch (err) { toast.error(extractError(err)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to, reloadKey]);

  const kstById = useMemo(() => Object.fromEntries(costCenters.map(k => [k.id, k])), [costCenters]);

  const visibleEmployees = useMemo(() => {
    return employees
      .filter(e => e.active)
      .filter(e => kstFilter === 'all' || e.cost_center_id === kstFilter)
      .filter(e => !search || `${e.first_name} ${e.last_name}`.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
  }, [employees, kstFilter, search]);

  const rangesByEmp = useMemo(() => {
    const m = {};
    for (const r of ranges) { (m[r.employee_id] = m[r.employee_id] || []).push(r); }
    return m;
  }, [ranges]);

  // Abwesende pro Tag
  const absentPerDay = useMemo(() => {
    const counts = new Array(days).fill(0);
    for (const r of ranges) {
      const s = new Date(r.start_date + 'T00:00:00');
      const e = new Date(r.end_date + 'T00:00:00');
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        if (d.getFullYear() === year && d.getMonth() === month) counts[d.getDate() - 1] += 1;
      }
    }
    return counts;
  }, [ranges, days, year, month]);

  const dayIndex = (iso) => {
    const d = new Date(iso + 'T00:00:00');
    if (d.getFullYear() !== year || d.getMonth() !== month) {
      if (d < new Date(year, month, 1)) return 0;
      return days - 1;
    }
    return d.getDate() - 1;
  };

  const isTodayMonth = now.getFullYear() === year && now.getMonth() === month;
  const todayIdx = isTodayMonth ? now.getDate() - 1 : -1;

  const contentWidth = NAME_W + days * cellW;

  const goPrev = () => { const m = month - 1; if (m < 0) { setMonth(11); setYear(year - 1); } else setMonth(m); };
  const goNext = () => { const m = month + 1; if (m > 11) { setMonth(0); setYear(year + 1); } else setMonth(m); };
  const goToday = () => { setYear(now.getFullYear()); setMonth(now.getMonth()); };

  const dayDates = Array.from({ length: days }, (_, i) => new Date(year, month, i + 1));

  const cellClick = (emp, dayNum) => {
    if (!canCreate) return;
    const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
    onOpenCreate?.({ employee_id: emp.id, start_date: iso });
  };

  return (
    <div className="space-y-3">
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={goToday} data-testid="timeline-today">Heute</Button>
          <div className="flex items-center">
            <Button size="icon" variant="ghost" onClick={goPrev} data-testid="timeline-prev"><ChevronLeft size={18} /></Button>
            <Button size="icon" variant="ghost" onClick={goNext} data-testid="timeline-next"><ChevronRight size={18} /></Button>
          </div>
          <div className="flex items-center gap-1">
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="w-36 h-8 text-sm font-semibold" data-testid="timeline-month"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS_DE.map((m, i) => <SelectItem key={i} value={String(i)}>{m}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-24 h-8 text-sm font-semibold" data-testid="timeline-year"><SelectValue /></SelectTrigger>
              <SelectContent>{[year - 2, year - 1, year, year + 1, year + 2].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Name suchen…" className="h-8 w-44 pl-7 text-sm" data-testid="timeline-search" />
          </div>
          <Select value={kstFilter} onValueChange={setKstFilter}>
            <SelectTrigger className="w-44 h-8 text-sm" data-testid="timeline-kst"><Filter size={13} className="mr-1" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Kostenstellen</SelectItem>
              {costCenters.filter(k => k.active !== false).map(k => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {canCreate && <Button size="sm" onClick={() => onOpenCreate?.({})} data-testid="timeline-add"><Plus size={14} className="mr-1" />Abwesenheit</Button>}
        </div>
      </div>

      {/* Grid
          Die Höhe ist begrenzt und die Tabelle scrollt in SICH, nicht mit der
          Seite. Nur dadurch kann der Tages-Kopf oben kleben bleiben: "sticky"
          richtet sich am nächsten scrollenden Element aus – war das die ganze
          Seite, wanderte der Kopf beim Scrollen einfach mit nach oben hinaus.
          (Nebenbei: overflow-x-auto allein macht aus dem Kasten schon einen
          Scroll-Container, aber einen ohne Höhenbegrenzung – es gab also nie
          etwas, woran der Kopf sich festhalten konnte.) */}
      <div ref={scrollRef}
           className="border border-slate-200 rounded-md overflow-auto bg-white
                      max-h-[calc(100vh-15rem)] md:max-h-[calc(100vh-13rem)]"
           data-testid="absence-timeline">
        <div className="relative" style={{ width: contentWidth, minWidth: '100%' }}>
          {/* Today line */}
          {todayIdx >= 0 && (
            <div className="absolute top-0 bottom-0 w-px bg-rose-500 z-20 pointer-events-none"
              style={{ left: NAME_W + todayIdx * cellW + cellW / 2 }}>
              <div className="absolute -top-0.5 -left-1 w-2 h-2 rounded-full bg-rose-500" />
            </div>
          )}

          {/* Header */}
          <div className="flex sticky top-0 z-30 bg-slate-50 border-b border-slate-200">
            <div className="sticky left-0 z-40 bg-slate-50 border-r border-slate-200 flex items-center px-3 text-xs font-semibold text-slate-600" style={{ width: NAME_W, minWidth: NAME_W }}>
              Mitarbeitende ({visibleEmployees.length})
            </div>
            {dayDates.map((d, i) => {
              const wd = (d.getDay() + 6) % 7; // 0=Mo
              const weekend = wd >= 5;
              const today = todayIdx === i;
              return (
                <div key={i} className={`flex flex-col items-center justify-center text-[11px] border-r border-slate-100 ${weekend ? 'bg-slate-100' : ''} ${today ? 'text-rose-600 font-bold' : 'text-slate-500'}`} style={{ width: cellW, minWidth: cellW, height: ROW_H }}>
                  <span className="font-bold text-slate-700">{String(d.getDate()).padStart(2, '0')}</span>
                  <span>{WEEKDAYS_SHORT[wd]}</span>
                </div>
              );
            })}
          </div>

          {/* Summary row – klebt direkt unter dem Tages-Kopf, damit man beim
              Durchscrollen der Namen immer sieht, wie viele an dem Tag fehlen.
              top = Höhe des Kopfes, sonst würde sie ihn überdecken. */}
          <div className="flex sticky z-20 border-b border-slate-200 bg-sky-50 border-slate-200"
               style={{ top: ROW_H }}>
            <div className="sticky left-0 z-30 bg-sky-50 border-r border-slate-200 flex items-center px-3 text-xs font-semibold text-slate-600" style={{ width: NAME_W, minWidth: NAME_W }}>
              Abwesende Personen
            </div>
            {absentPerDay.map((c, i) => {
              const d = dayDates[i]; const wd = (d.getDay() + 6) % 7; const weekend = wd >= 5;
              return (
                <div key={i} className={`flex items-center justify-center border-r border-slate-100 ${weekend ? 'bg-slate-100' : ''}`} style={{ width: cellW, minWidth: cellW, height: 26 }}>
                  {c > 0 && <span className="text-[11px] font-bold text-sky-700 bg-sky-100 rounded px-1">{c}</span>}
                </div>
              );
            })}
          </div>

          {/* Rows */}
          {loading && <div className="p-6 text-sm text-slate-500">Lädt…</div>}
          {!loading && visibleEmployees.map(emp => {
            const kst = kstById[emp.cost_center_id];
            const empRanges = rangesByEmp[emp.id] || [];
            return (
              <div key={emp.id} className="flex border-b border-slate-100 hover:bg-slate-50/60" style={{ height: ROW_H }} data-testid={`timeline-row-${emp.id}`}>
                <div className="sticky left-0 z-10 bg-white border-r border-slate-200 flex flex-col justify-center px-3 leading-tight" style={{ width: NAME_W, minWidth: NAME_W }}>
                  <span className="text-sm font-semibold text-slate-800 truncate">{emp.first_name} {emp.last_name}</span>
                  <span className="text-[11px] text-slate-500 truncate">{kst?.name || '—'}</span>
                </div>
                <div className="relative" style={{ width: days * cellW }}>
                  {/* day cells */}
                  <div className="absolute inset-0 flex">
                    {dayDates.map((d, i) => {
                      const wd = (d.getDay() + 6) % 7; const weekend = wd >= 5;
                      return <div key={i} onClick={() => cellClick(emp, i + 1)} className={`border-r border-slate-100 ${weekend ? 'bg-slate-100/70' : ''} ${canCreate ? 'cursor-pointer hover:bg-sky-50' : ''}`} style={{ width: cellW }} />;
                    })}
                  </div>
                  {/* bars */}
                  {empRanges.map((r, ri) => {
                    const s = dayIndex(r.start_date);
                    const e = dayIndex(r.end_date);
                    const span = Math.max(1, e - s + 1);
                    return (
                      <button key={ri} onClick={() => onOpenEdit?.(r)} title={`${r.type} · ${r.start_date} – ${r.end_date}`}
                        className={`absolute rounded text-[11px] font-semibold px-1.5 truncate flex items-center ${TYPE_BAR[r.type] || 'bg-slate-400 text-white'} ${r.source === 'pattern' ? 'opacity-80' : ''}`}
                        style={{ left: s * cellW + 1, width: span * cellW - 2, top: 6, height: ROW_H - 12, zIndex: 5 }}
                        data-testid={`timeline-bar-${emp.id}-${ri}`}>
                        {r.type}{r.half_day ? ' ½' : ''}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {!loading && visibleEmployees.length === 0 && <div className="p-6 text-sm text-slate-500">Keine Mitarbeiter für die Filter.</div>}
        </div>
      </div>
    </div>
  );
}
